// 개발용 — Agent 서버 SSE 요청/응답을 시간순으로 모아 디버그 패널에서 확인.
// 운영 코드 경로에는 영향 없음. 사용 끝나면 본 파일 + AgentDebugPanel +
// 마운트 지점 + apiClient 의 capture 호출만 제거하면 끝.

import type { ResumeRequest, SSEEvent, StreamRequest } from "./types";

export type DebugEntryStatus = "streaming" | "done" | "error";

export interface DebugEvent {
  at: number; // 수신 시각 (epoch ms)
  event: SSEEvent;
}

interface DebugEntryBase {
  id: string;
  at: number;
  events: DebugEvent[];
  status: DebugEntryStatus;
  error?: string;
}

export type DebugEntry =
  | (DebugEntryBase & { kind: "stream"; body: StreamRequest })
  | (DebugEntryBase & { kind: "resume"; body: ResumeRequest });

const MAX_ENTRIES = 100;

let entries: DebugEntry[] = [];
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

const rid = () =>
  Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);

export function startStreamEntry(body: StreamRequest): string {
  const entry: DebugEntry = {
    id: rid(),
    kind: "stream",
    at: Date.now(),
    body,
    events: [],
    status: "streaming",
  };
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  notify();
  return entry.id;
}

export function startResumeEntry(body: ResumeRequest): string {
  const entry: DebugEntry = {
    id: rid(),
    kind: "resume",
    at: Date.now(),
    body,
    events: [],
    status: "streaming",
  };
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  notify();
  return entry.id;
}

export function appendEvent(entryId: string, event: SSEEvent): void {
  let touched = false;
  entries = entries.map((e) => {
    if (e.id !== entryId) return e;
    touched = true;
    return { ...e, events: [...e.events, { at: Date.now(), event }] };
  });
  if (touched) notify();
}

export function finishEntry(
  entryId: string,
  status: DebugEntryStatus,
  error?: string
): void {
  let touched = false;
  entries = entries.map((e) => {
    if (e.id !== entryId) return e;
    touched = true;
    return { ...e, status, error };
  });
  if (touched) notify();
}

export function getDebugEntries(): DebugEntry[] {
  return entries;
}

export function clearDebug(): void {
  entries = [];
  notify();
}

export function subscribeDebug(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
